<template>
  <AppLayout>
    <div class="transaction">
      <section id="transaction-products">
        <div class="container">
        
        <h6 style="margin-bottom: 0px;" class="fw-bold">All Tender Submissions</h6>
        
        <!-- Stats Cards Section -->
        <div class="warehouse-inventory-card-wrapper">
          <div class="warehouse-inventory-card">
            <p>New <br>
              Submissions</p>
            <strong>{{ stats.submitted || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Under <br>
              Review</p>
            <strong class="green">{{ stats.underReview || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Shortlisted <br>
              Applications</p>
            <strong class="green">{{ stats.shortlisted || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Total <br>
              Submissions</p>
            <strong>{{ stats.total || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Rejected <br>
              Applications</p>
            <strong class="red">{{ stats.rejected || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Awarded <br>
              Contracts</p>
            <strong class="green">{{ stats.awarded || 0 }}</strong>
          </div>
        </div>

        <!-- Filter Section -->
        <div class="filter-search" style="margin-top: 0px;padding-left: 0px;">
          <div class="filter-search-header">
            <h6>Filter </h6>
            <div class="header-actions">
              <button class="clear" @click="clearFilters">Clear All</button>
            </div>
          </div>
          <div class="filter-search-body">
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label for="search">Search Submissions</label>
                  <input type="text" class="form-control" v-model="searchParams.search" @input="debouncedSearch" placeholder="Search by company name, tender title..." />
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label for="status">Status</label>
                  <select class="full" v-model="searchParams.status" @change="fetchSubmissions">
                    <option value="">All Status</option>
                    <option value="SUBMITTED">Submitted</option>
                    <option value="UNDER_REVIEW">Under Review</option>
                    <option value="SHORTLISTED">Shortlisted</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="AWARDED">Awarded</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label for="tender">Tender</label>
                  <select class="full" v-model="searchParams.tenderId" @change="fetchSubmissions">
                    <option value="">All Tenders</option>
                    <option v-for="tender in tenders" :key="tender.id" :value="tender.id">
                      {{ tender.referenceNumber }} - {{ tender.title }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <button class="btn btn-primary small" style="margin-top: 32px;" type="button" @click="fetchSubmissions">Search</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submissions Table -->
        <div class="transaction-products-list" style="margin-top: 0px;">
          <!-- Skeleton loader for submissions table -->
          <SkeletonLoader v-if="loading" type="table" :rows="8" :columns="7" />
          
          <table v-else id="submissionsTable" class="table table-hover">
            <thead style="background-color: #f8f9fa; border-bottom: 2px solid #dee2e6;">
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">No.</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Company</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Tender</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Submitted</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Value</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Status</th>
              <th style="padding: 15px 12px; font-weight: 600; font-size: 12px; color: #495057; text-align: left; border-bottom: none;">Actions</th>
            </thead>
            <tbody v-if="!loading && submissions.length > 0">
              <tr v-for="(submission, index) in submissions" :key="submission.id" @click="viewSubmission(submission)" style="cursor: pointer;">
                <td>{{ index + 1 }}</td>
                <td>
                  <div>
                    <p>{{ submission.companyName }}</p>
                    <small>{{ submission.contactPerson }}</small>
                  </div>
                </td>
                <td>
                  <div>
                    <p>{{ submission.tender?.title }}</p>
                    <small>{{  submission.tender?.referenceNumber}}</small>
                  </div>
                </td>
                <td>{{ formatDate(submission.submittedAt) }}</td>
                <td>{{ submission.proposedValue ? formatCurrency(submission.proposedValue) : 'N/A' }}</td>
                <td>
                  <span :class="`badge badge-${getStatusClass(submission.status)}`">
                    {{ submission.status }}
                  </span>
                </td>
                <td>
                  <div class="btn-group" role="group">
                    <button
                      @click.stop="viewSubmission(submission)"
                      class="btn btn-sm btn-outline-primary"
                      title="View Submission"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    <button
                      @click.stop="updateStatus(submission.id, 'UNDER_REVIEW')"
                      v-if="submission.status === 'SUBMITTED'"
                      class="btn btn-sm btn-outline-info"
                      title="Start Review"
                    >
                      <i class="fas fa-search"></i>
                    </button>
                    <button
                      @click.stop="updateStatus(submission.id, 'SHORTLISTED')"
                      v-if="submission.status === 'UNDER_REVIEW'"
                      class="btn btn-sm btn-outline-success"
                      title="Shortlist"
                    >
                      <i class="fas fa-star"></i>
                    </button>
                    <button
                      @click.stop="updateStatus(submission.id, 'REJECTED')"
                      v-if="['SUBMITTED', 'UNDER_REVIEW'].includes(submission.status)"
                      class="btn btn-sm btn-outline-danger"
                      title="Reject"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty State -->
          <div v-if="!loading && submissions.length === 0" class="empty-state text-center py-5">
            <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No submissions found</h5>
            <p class="text-muted">No applications match your current filters</p>
          </div>

        </div>
      </div>
    </section>
  </div>
</AppLayout>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../../components/AppLayout.vue'
import SkeletonLoader from '../../components/SkeletonLoader'
import { tenderApi } from '@/api/tender'

// Simple utility functions
const showToast = (message, type = 'info') => {
  console.log(`${type.toUpperCase()}: ${message}`)
}

const debounce = (func, delay) => {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

export default {
  name: 'AllTenderSubmissions',
  components: {
    AppLayout,
    SkeletonLoader
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const selectedSubmission = ref(null)
    
    const stats = reactive({
      total: 0,
      submitted: 0,
      underReview: 0,
      shortlisted: 0,
      rejected: 0,
      awarded: 0,
    })
    
    const searchParams = reactive({
      search: '',
      status: '',
      tenderId: '',
      page: 1,
      limit: 20,
    })
    
    const submissions = ref([])
    const tenders = ref([])
    const totalSubmissions = ref(0)
    const totalPages = ref(0)
    
    const currentPage = computed(() => searchParams.page)
    
    const fetchSubmissions = async () => {
      loading.value = true
      try {
        // Clean params: remove empty strings/null to avoid backend validation errors
        const params = {}
        Object.keys(searchParams).forEach(k => {
          const v = searchParams[k]
          if (v !== undefined && v !== null && v !== '') params[k] = v
        })
        const response = await tenderApi.getAllSubmissions(params)
        // backend returns { data: [...], total, page, limit, totalPages }
        const payload = response && response.data ? response.data : response
        if (payload && Array.isArray(payload.data)) {
          submissions.value = payload.data
        } else if (Array.isArray(payload)) {
          submissions.value = payload
        } else if (Array.isArray(response.data)) {
          submissions.value = response.data
        } else {
          submissions.value = []
        }

        totalSubmissions.value = (response && response.total) || (response && response.data && response.data.total) || 0
        totalPages.value = (response && response.totalPages) || (response && response.data && response.data.totalPages) || 0
        
        // Calculate stats
        calculateStats()
      } catch (error) {
        console.error('Error fetching submissions:', error)
        showToast('Error loading submissions', 'error')
      } finally {
        loading.value = false
      }
    }
    
    const fetchTenders = async () => {
      try {
        const response = await tenderApi.getAllTenders({ limit: 100 })
        // backend returns { data: [...] }
        if (response && Array.isArray(response.data)) {
          tenders.value = response.data
        } else if (response && response.data && Array.isArray(response.data.data)) {
          tenders.value = response.data.data
        } else if (Array.isArray(response)) {
          tenders.value = response
        } else {
          tenders.value = []
        }
      } catch (error) {
        console.error('Error fetching tenders:', error)
      }
    }
    
    const calculateStats = () => {
      stats.total = submissions.value.length
      stats.submitted = submissions.value.filter(s => s.status === 'SUBMITTED').length
      stats.underReview = submissions.value.filter(s => s.status === 'UNDER_REVIEW').length
      stats.shortlisted = submissions.value.filter(s => s.status === 'SHORTLISTED').length
      stats.rejected = submissions.value.filter(s => s.status === 'REJECTED').length
      stats.awarded = submissions.value.filter(s => s.status === 'AWARDED').length
    }
    
    const debouncedSearch = debounce(() => {
      searchParams.page = 1
      fetchSubmissions()
    }, 500)
    
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        searchParams.page = page
        fetchSubmissions()
      }
    }
    
    const viewSubmission = (submission) => {
      selectedSubmission.value = submission
    }
    
    const viewSubmissionDetail = (submission) => {
      router.push(`/admin/tenders/${submission.tender.id}/submissions`)
    }
    
    const goToFullSubmission = () => {
      if (selectedSubmission.value) {
        router.push(`/admin/tenders/${selectedSubmission.value.tender.id}/submissions`)
      }
    }
    
    const quickStatusUpdate = async (submission, status) => {
      try {
        await tenderApi.updateSubmissionStatus(submission.id, { status })
        showToast(`Application ${status.toLowerCase().replace('_', ' ')}`, 'success')
        fetchSubmissions()
      } catch (error) {
        console.error('Error updating status:', error)
        showToast('Error updating application status', 'error')
      }
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }
    
    const formatCurrency = (amount, currency = 'USD') => {
      if (!amount) return 'N/A'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount)
    }

    const getStatusClass = (status) => {
      const statusMap = {
        'SUBMITTED': 'primary',
        'UNDER_REVIEW': 'info', 
        'SHORTLISTED': 'success',
        'REJECTED': 'danger',
        'AWARDED': 'success'
      }
      return statusMap[status] || 'secondary'
    }

    const clearFilters = () => {
      searchParams.search = ''
      searchParams.status = ''
      searchParams.tenderId = ''
      searchParams.page = 1
      fetchSubmissions()
    }

    const updateStatus = async (submissionId, newStatus) => {
      try {
        await tenderApi.updateSubmissionStatus(submissionId, newStatus)
        showToast('Submission status updated successfully', 'success')
        fetchSubmissions()
        calculateStats()
      } catch (error) {
        showToast('Error updating submission status', 'error')
        console.error('Error updating status:', error)
      }
    }
    
    onMounted(() => {
      fetchTenders()
      fetchSubmissions()
    })
    
    return {
      loading,
      selectedSubmission,
      stats,
      searchParams,
      submissions,
      tenders,
      totalSubmissions,
      totalPages,
      currentPage,
      debouncedSearch,
      goToPage,
      viewSubmission,
      viewSubmissionDetail,
      goToFullSubmission,
      quickStatusUpdate,
      formatDate,
      formatCurrency,
      getStatusClass,
      clearFilters,
      updateStatus,
    }
  },
}
</script>

<style scoped>
/* Reuse styles from HomeTransactions.vue */
.transaction {
  min-height: 100vh;
  background-color: #f8f9fa;
}

#transaction-products {
  padding: 20px 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Stats Cards */
.warehouse-inventory-card-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  margin: 30px 0;
}

.warehouse-inventory-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.warehouse-inventory-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.warehouse-inventory-card p {
  margin: 0 0 12px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.4;
}

.warehouse-inventory-card strong {
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.warehouse-inventory-card strong.green {
  color: #28a745;
}

.warehouse-inventory-card strong.red {
  color: #dc3545;
}



.filter-search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 12px 12px 0 0;
}

.filter-search-header strong {
  font-size: 18px;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.clear {
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  border: none;
  background: none;
  padding: 0;
}

.clear:hover {
  color: #374151;
  text-decoration: underline;
}

.filter-search-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select.full {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

/* Products List */
.products-list {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.products-list strong {
  font-size: 18px;
  color: #1f2937;
  margin-bottom: 20px;
  display: block;
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  padding: 15px 12px;
  font-weight: 600;
  font-size: 12px;
  color: #495057;
  text-align: left;
}

.table td {
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  font-size: 14px;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Buttons */
.btn {
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-small {
  padding: 6px 12px;
  font-size: 12px;
}

/* Badges */
.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.badge-primary { background: #dbeafe; color: #1d4ed8; }
.badge-info { background: #dbeafe; color: #0284c7; }
.badge-success { background: #dcfce7; color: #16a34a; }
.badge-danger { background: #fee2e2; color: #dc2626; }
.badge-secondary { background: #f1f5f9; color: #64748b; }

/* Responsive */
@media (max-width: 768px) {
  .warehouse-inventory-card-wrapper {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .filter-search-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .warehouse-inventory-card-wrapper {
    grid-template-columns: 1fr;
  }
}

.header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.header p {
  color: #666;
  margin: 0;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.stat-icon.submitted { background: #3b82f6; }
.stat-icon.review { background: #f59e0b; }
.stat-icon.shortlisted { background: #10b981; }
.stat-icon.total { background: #6b7280; }

.stat-content h3 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1a1a1a;
}

.stat-content p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.filters-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-controls {
  display: flex;
  gap: 14px;
  align-items: center;
}

.search-input {
  position: relative;
  flex: 1;
}

.search-input i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.search-input input {
  width: 100%;
  padding: 12px 14px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
}

.search-input input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select {
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-width: 160px;
}

.submissions-table {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr;
  gap: 14px;
  padding: 14px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.header-cell {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background: #f8fafc;
}

.table-cell {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.company-info h4,
.tender-info h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 2px 0;
  color: #1a1a1a;
}

.company-info p,
.tender-info p {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.status {
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.status.submitted { background: #e0f2fe; color: #0288d1; }
.status.under_review { background: #fff3e0; color: #f57c00; }
.status.shortlisted { background: #e8f5e8; color: #2e7d32; }
.status.rejected { background: #ffebee; color: #d32f2f; }
.status.awarded { background: #f3e5f5; color: #7b1fa2; }

.score {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #f59e0b;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 11px;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-outline {
  border: 1px solid #e5e7eb;
  color: #374151;
  background: white;
}

.btn-outline:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.text-muted {
  color: #9ca3af;
}

.loading {
  text-align: center;
  padding: 48px;
  color: #666;
}

.loading i {
  font-size: 24px;
  margin-bottom: 14px;
}

.empty-state {
  text-align: center;
  padding: 48px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 14px;
  color: #d1d5db;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-top: 24px;
}

.page-info {
  color: #666;
  font-size: 14px;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #1a1a1a;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item strong {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.detail-item span {
  font-size: 14px;
  color: #666;
}

.detail-section h3 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #1a1a1a;
}

.detail-section p {
  color: #374151;
  line-height: 1.6;
  margin: 0;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
}

@media (max-width: 768px) {
  .filter-controls {
    flex-direction: column;
    gap: 12px;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .table-header {
    display: none;
  }
  
  .table-row {
    display: flex;
    flex-direction: column;
    padding: 14px;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>